/**
 * Central registry of legal agreements the platform requires users to accept.
 *
 * This is the single source of truth for agreement metadata, versions, and the
 * acceptance language shown at signup / login.
 *
 * Each agreement's content is an actual PDF file served from `public/legal/`.
 * The on-screen review experience, the "Download PDF" button, and the
 * advance-notice email attachment all reference the same PDF (`pdfUrl`), so
 * dropping the finalized PDF into `public/legal/` updates every surface at once.
 *
 * IMPORTANT (versioning): when the agreement PDF materially changes, bump the
 * `version` string. Bumping a required agreement's version will re-prompt every
 * affected user for acceptance on their next login (see `getOutstandingAgreements`).
 */

export type AgreementType =
  | 'shopper_platform_participation'
  | 'terms_of_service';

export interface Agreement {
  /** Stable identifier persisted in the acceptance log. */
  type: AgreementType;
  /** Human-readable title shown in the UI. */
  title: string;
  /**
   * Optional shorter label for compact surfaces (e.g. the "View … (PDF)"
   * button). Falls back to `title` when unset.
   */
  shortTitle?: string;
  /**
   * Version of this document. Bump when the document materially changes to
   * re-trigger acceptance for existing users.
   */
  version: string;
  /** Effective / last-updated date shown alongside the document. */
  effectiveDate: string;
  /**
   * Public URL of the agreement PDF (served statically from `public/legal/`),
   * for agreements whose content is a downloadable PDF. Replace the file at
   * this path with the finalized agreement PDF.
   */
  pdfUrl?: string;
  /** Suggested filename when the user downloads the PDF. */
  pdfFilename?: string;
  /**
   * URL of a site-hosted version of the document (e.g. an existing page),
   * used for agreements that are not distributed as a PDF.
   */
  linkUrl?: string;
}

/**
 * Public URL of the Terms of Service PDF (served statically from `public/legal/`).
 * Exported so marketing/footer links and the agreement registry all point at the
 * same file — dropping a new PDF at this path updates every surface at once.
 */
export const TERMS_OF_SERVICE_PDF_URL =
  '/legal/sourced-by-terms-of-service.pdf';

export const AGREEMENTS: Record<AgreementType, Agreement> = {
  shopper_platform_participation: {
    type: 'shopper_platform_participation',
    title: 'Shopper Platform Participation Agreement',
    shortTitle: 'Shopper Agreement',
    version: '1',
    effectiveDate: 'June 25, 2025',
    pdfUrl: '/legal/sourced-by-shopper-platform-participation-agreement.pdf',
    pdfFilename: 'SourcedBy-Shopper-Platform-Participation-Agreement.pdf',
  },
  terms_of_service: {
    type: 'terms_of_service',
    title: 'Terms of Service',
    version: '1',
    effectiveDate: 'April 28, 2025',
    pdfUrl: TERMS_OF_SERVICE_PDF_URL,
    pdfFilename: 'SourcedBy-Terms-of-Service.pdf',
  },
};

/**
 * Acceptance language presented next to the checkbox. Kept here (rather than
 * inline in components) so the exact wording can be retained in the acceptance
 * log for compliance.
 */
export const SHOPPER_ACCEPTANCE_TEXT =
  'By checking this box and clicking "I Agree," you agree to the Shopper ' +
  'Platform Participation Agreement and Terms of Service.';

export const GENERAL_ACCEPTANCE_TEXT =
  'By checking this box and clicking "I Agree," you agree to the Terms of Service.';

/**
 * The agreements a shopper must have accepted to use the platform. This is the
 * set the login gate enforces and the set recorded at shopper signup.
 */
export const SHOPPER_REQUIRED_AGREEMENTS: AgreementType[] = [
  'shopper_platform_participation',
  'terms_of_service',
];

/**
 * Agreements required at signup for a given role. Sourcers accept the Terms of
 * Service; shoppers additionally accept the Shopper Platform Participation
 * Agreement.
 */
export function getRequiredAgreementsForRole(
  role: string | null | undefined
): AgreementType[] {
  if (role === 'shopper' || role === 'shopper_guest') {
    return [...SHOPPER_REQUIRED_AGREEMENTS];
  }
  return ['terms_of_service'];
}

export function getAcceptanceTextForRole(
  role: string | null | undefined
): string {
  if (role === 'shopper' || role === 'shopper_guest') {
    return SHOPPER_ACCEPTANCE_TEXT;
  }
  return GENERAL_ACCEPTANCE_TEXT;
}

export function getAgreement(type: AgreementType): Agreement {
  return AGREEMENTS[type];
}

export function isShopperRole(role: string | null | undefined): boolean {
  return role === 'shopper' || role === 'shopper_guest';
}
